import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
  Divider,
  alpha,
  useTheme,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  CheckCircle as CompletedIcon,
  Schedule as PendingIcon,
  HourglassEmpty as ProcessingIcon,
  Error as ErrorIcon,
  Upload as FileUploadIcon,
  Lightbulb as TopicAnalysisIcon,
  Hub as SemanticIcon,
  Settings as BatchConfigIcon,
  PlayArrow as BatchRunIcon,
  Search as SerpToolIcon,
  TrendingUp as DataForSeoIcon,
  Dashboard as ExploreResultsIcon,
  Info as InfoIcon,
  ArrowForward as ArrowForwardIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { workflowService, DatabaseWorkflow } from '../../services/api/workflowService';
import { useWorkflowContext } from '../../context/WorkflowContext';
import { snowflakeService } from '../../services/snowflakeService';

interface ModuleSummary {
  type: string;
  name: string;
  status: string;
  count: number;
  icon: React.ReactNode;
  path: string;
  stepIndex: number;
}

interface WorkflowStatusMetrics {
  workflow_id: string;
  client_files_ingestion_status_metrics: any[];
  ingestion_status_metrics: any[];
  client_files_count: number;
  ingestion_count: number;
}

const WorkflowSummaryStep: React.FC = () => {
  const { clientId, workflowId } = useParams<{
    clientId: string;
    workflowId: string;
  }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const contextData = useWorkflowContext();

  const [workflow, setWorkflow] = useState<DatabaseWorkflow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [statusMetrics, setStatusMetrics] = useState<WorkflowStatusMetrics | null>(null);
  const [metricsError, setMetricsError] = useState<string | null>(null);

  const workflowName = contextData.workflowName || contextData.workflow?.name || `Workflow ${workflowId}`;
  const clientName = contextData.clientName || `Client ${clientId}`;

  // Step definitions with icons and paths
  const stepDefinitions = [
    {
      type: 'fileupload_v2',
      name: 'File Upload',
      icon: <FileUploadIcon />,
      path: '',
      stepIndex: 0,
    },
    {
      type: 'module1_v2',
      name: 'Query Analysis',
      icon: <TopicAnalysisIcon />,
      path: 'keyword',
      stepIndex: 1,
    },
    {
      type: 'module3_v2',
      name: 'Intent Analysis',
      icon: <SemanticIcon />,
      path: 'semantic',
      stepIndex: 2,
    },
    {
      type: 'module4-config_v2',
      name: 'Batch Configuration',
      icon: <BatchConfigIcon />,
      path: 'batch_config',
      stepIndex: 3,
    },
    {
      type: 'module4_v2',
      name: 'Batch Processing',
      icon: <BatchRunIcon />,
      path: 'batch_run',
      stepIndex: 4,
    },
    {
      type: 'serp-analysis_v2',
      name: 'SERP API Tool',
      icon: <SerpToolIcon />,
      path: 'serp_tool',
      stepIndex: 5,
    },
    {
      type: 'dataforseo-analysis_v2',
      name: 'DataForSEO Volume Analysis',
      icon: <DataForSeoIcon />,
      path: 'data_for_seo',
      stepIndex: 6,
    },
  ];

  useEffect(() => {
    const fetchWorkflowData = async () => {
      if (!workflowId) {
        setError('No workflow ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const workflowData = await workflowService.getDbWorkflow(workflowId);
        setWorkflow(workflowData);
      } catch (err: any) {
        console.error('Error fetching workflow data:', err);
        setError(err.message || 'Failed to load workflow data');
      } finally {
        setLoading(false);
      }
    };

    fetchWorkflowData();
  }, [workflowId]);

  const POLL_INTERVAL_MS = 15_000;

  const allSectionsCompleted = (metrics: WorkflowStatusMetrics | null): boolean => {
    if (!metrics) return false;
    const isCompleted = (type: string) => {
      const record = metrics.ingestion_status_metrics.find(
        (r: any) => r.output_type?.toLowerCase() === type
      );
      return !!record && record.status === 'LOAD_COMPLETED';
    };
    return (
      isCompleted('workflow_results') &&
      isCompleted('serp_results') &&
      isCompleted('dataforseo_results')
    );
  };

  const fetchStatusMetrics = async (isPolling = false) => {
    if (!workflowId) {
      setMetricsError('No workflow ID provided');
      setMetricsLoading(false);
      return;
    }

    try {
      if (!isPolling) setMetricsLoading(true);
      setMetricsError(null);
      const response = await snowflakeService.getWorkflowStatusMetrics(workflowId);

      if (response.success && response.data) {
        setStatusMetrics(response.data);
      } else {
        setMetricsError(response.error || 'Failed to load status metrics');
      }
    } catch (err: any) {
      console.error('Error fetching status metrics:', err);
      setMetricsError(err.message || 'Failed to load status metrics');
    } finally {
      if (!isPolling) setMetricsLoading(false);
    }
  };

  useEffect(() => {
    fetchStatusMetrics();
  }, [workflowId]);

  // Auto-poll until all three sections reach LOAD_COMPLETED
  useEffect(() => {
    if (allSectionsCompleted(statusMetrics)) return;

    const timer = setInterval(() => {
      fetchStatusMetrics(true);
    }, POLL_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [statusMetrics, workflowId]);

  const handleRefresh = () => {
    fetchStatusMetrics();
  };

  const getModuleSummaries = (): ModuleSummary[] => {
    if (!workflow?.modules) return [];

    const summaries: ModuleSummary[] = [];

    stepDefinitions.forEach((stepDef) => {
      const modules = workflow.modules?.filter((m) => m.type === stepDef.type || m.module_type === stepDef.type);
      const latestModule = modules && modules.length > 0 ? modules[0] : null;

      summaries.push({
        type: stepDef.type,
        name: stepDef.name,
        status: latestModule?.status || 'not_started',
        count: modules?.length || 0,
        icon: stepDef.icon,
        path: stepDef.path,
        stepIndex: stepDef.stepIndex,
      });
    });

    return summaries;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CompletedIcon sx={{ color: 'success.main' }} />;
      case 'processing':
        return <ProcessingIcon sx={{ color: 'warning.main' }} />;
      case 'failed':
        return <ErrorIcon sx={{ color: 'error.main' }} />;
      case 'not_started':
        return <PendingIcon sx={{ color: 'grey.400' }} />;
      default:
        return <PendingIcon sx={{ color: 'grey.400' }} />;
    }
  };

  const getStatusChip = (status: string) => {
    const statusConfig: Record<
      string,
      { label: string; color: 'success' | 'warning' | 'error' | 'default' | 'primary' | 'secondary' | 'info' }
    > = {
      completed: { label: 'Completed', color: 'success' },
      processing: { label: 'Processing', color: 'warning' },
      failed: { label: 'Failed', color: 'error' },
      not_started: { label: 'Not Started', color: 'default' },
    };

    const config = statusConfig[status] || statusConfig.not_started;
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  // Helper function to safely render metric values
  const renderMetricValue = (value: any): string => {
    if (value === null || value === undefined) {
      return 'N/A';
    }
    if (typeof value === 'number') {
      return value.toLocaleString();
    }
    if (typeof value === 'string') {
      return value;
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    if (Array.isArray(value)) {
      return `[${value.length} items]`;
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return String(value);
  };

  const calculateProgress = () => {
    const summaries = getModuleSummaries();
    if (summaries.length === 0) return 0;

    const completedCount = summaries.filter((s) => s.status === 'completed').length;
    return Math.round((completedCount / summaries.length) * 100);
  };

  const handleNavigateToStep = (path: string) => {
    const basePath = `/workflow2/${clientId}/${workflowId}`;
    const targetPath = path ? `${basePath}/${path}` : basePath;
    navigate(targetPath);
  };

  const handleNavigateToExplore = () => {
    const explorePath = `/workflow2/${clientId}/${workflowId}/explore`;
    navigate(explorePath);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  const moduleSummaries = getModuleSummaries();
  const progress = calculateProgress();
  const completedSteps = moduleSummaries.filter((s) => s.status === 'completed').length;
  const totalSteps = moduleSummaries.length;

  return (
    <Box sx={{ width: '100%' }}>
      {/* Snowflake Status Metrics Section */}
      <Paper elevation={2} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Snowflake Ingestion Status Metrics
          </Typography>
          <Tooltip title="Refresh metrics" placement="left">
            <IconButton
              onClick={handleRefresh}
              disabled={metricsLoading}
              color="primary"
              sx={{
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                },
              }}
            >
              <RefreshIcon
                sx={{
                  animation: metricsLoading ? 'spin 1s linear infinite' : 'none',
                  '@keyframes spin': {
                    '0%': {
                      transform: 'rotate(0deg)',
                    },
                    '100%': {
                      transform: 'rotate(360deg)',
                    },
                  },
                }}
              />
            </IconButton>
          </Tooltip>
        </Box>

        {metricsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress size={30} />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
              Loading metrics...
            </Typography>
          </Box>
        ) : metricsError ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {metricsError}
          </Alert>
        ) : statusMetrics ? (
          <>
            {/* Summary Statistics */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card 
                  variant="outlined" 
                  sx={{ 
                    textAlign: 'center', 
                    p: 2,
                    backgroundColor: alpha(theme.palette.primary.main, 0.02),
                  }}
                >
                  <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                    Total Files
                  </Typography>
                  <Typography variant="h5" fontWeight={600} color="primary.main">
                    {statusMetrics.client_files_count}
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card 
                  variant="outlined" 
                  sx={{ 
                    textAlign: 'center', 
                    p: 2,
                    backgroundColor: alpha(theme.palette.success.main, 0.02),
                  }}
                >
                  <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                    Total Rows Ingested
                  </Typography>
                  <Typography variant="h5" fontWeight={600} color="success.main">
                    {statusMetrics.client_files_ingestion_status_metrics
                      .reduce((sum: number, file: any) => sum + (file.number_of_rows || 0), 0)
                      .toLocaleString()}
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card 
                  variant="outlined" 
                  sx={{ 
                    textAlign: 'center', 
                    p: 2,
                    backgroundColor: alpha(theme.palette.info.main, 0.02),
                  }}
                >
                  <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                    Output Types
                  </Typography>
                  <Typography variant="h5" fontWeight={600} color="info.main">
                    {statusMetrics.ingestion_count}
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card 
                  variant="outlined" 
                  sx={{ 
                    textAlign: 'center', 
                    p: 2,
                    backgroundColor: alpha(theme.palette.secondary.main, 0.02),
                  }}
                >
                  <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                    Completed
                  </Typography>
                  <Typography variant="h5" fontWeight={600} color="secondary.main">
                    {statusMetrics.client_files_ingestion_status_metrics.filter(
                      (f: any) => f.status === 'LOAD_COMPLETED'
                    ).length}/{statusMetrics.client_files_count}
                  </Typography>
                </Card>
              </Grid>
            </Grid>

            <Grid container spacing={3}>
            {/* Client Files Ingestion Status Metrics */}
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                    Client Files Ingestion Status
                  </Typography>

                  {statusMetrics.client_files_ingestion_status_metrics.length > 0 ? (
                    <TableContainer sx={{ maxHeight: 500 }}>
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600, backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                              File Category
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                              File Name
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                              Rows
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                              Metrics
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                              Duration
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                              Status
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                              Ingested At
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {statusMetrics.client_files_ingestion_status_metrics.map((file: any, index: number) => {
                            let parsedMetrics: any = {};
                            try {
                              parsedMetrics = typeof file.metrics === 'string' ? JSON.parse(file.metrics) : file.metrics;
                            } catch (e) {
                              console.error('Failed to parse metrics:', e);
                            }

                            return (
                              <TableRow
                                key={file.id || index}
                                sx={{
                                  '&:hover': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.02),
                                  },
                                }}
                              >
                                <TableCell>
                                  <Chip
                                    label={file.file_category || 'N/A'}
                                    size="small"
                                    sx={{
                                      backgroundColor: alpha(theme.palette.info.main, 0.1),
                                      color: 'info.main',
                                      fontWeight: 500,
                                    }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Tooltip title={file.file_name || 'N/A'} placement="top">
                                    <Typography 
                                      variant="body2" 
                                      sx={{ 
                                        maxWidth: 250, 
                                        overflow: 'hidden', 
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        cursor: 'help',
                                      }}
                                    >
                                      {file.file_name || 'N/A'}
                                    </Typography>
                                  </Tooltip>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" fontWeight={500}>
                                    {file.number_of_rows?.toLocaleString() || 0}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    {parsedMetrics.total_keywords && (
                                      <Typography variant="caption" color="text.secondary">
                                        Keywords: <strong>{renderMetricValue(parsedMetrics.total_keywords)}</strong>
                                      </Typography>
                                    )}
                                    {parsedMetrics.total_impressions && (
                                      <Typography variant="caption" color="text.secondary">
                                        Impressions: <strong>{renderMetricValue(parsedMetrics.total_impressions)}</strong>
                                      </Typography>
                                    )}
                                    {parsedMetrics.total_clicks !== undefined && (
                                      <Typography variant="caption" color="text.secondary">
                                        Clicks: <strong>{renderMetricValue(parsedMetrics.total_clicks)}</strong>
                                      </Typography>
                                    )}
                                    {parsedMetrics.total_costs !== undefined && parsedMetrics.total_costs > 0 && (
                                      <Typography variant="caption" color="text.secondary">
                                        Costs: <strong>${renderMetricValue(parsedMetrics.total_costs)}</strong>
                                      </Typography>
                                    )}
                                  </Box>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" fontWeight={500}>
                                    {file.duration_mins || 'N/A'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={file.status || 'Unknown'}
                                    size="small"
                                    color={file.status === 'LOAD_COMPLETED' ? 'success' : 'default'}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="caption" color="text.secondary">
                                    {file.ingested_at
                                      ? new Date(file.ingested_at).toLocaleString('en-US', {
                                          month: 'short',
                                          day: 'numeric',
                                          hour: '2-digit',
                                          minute: '2-digit',
                                        })
                                      : 'N/A'}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No records found
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Workflow Results */}
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                    Workflow Results
                  </Typography>

                  {statusMetrics.ingestion_status_metrics.filter((r: any) => r.output_type?.toLowerCase() === 'workflow_results').length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {statusMetrics.ingestion_status_metrics
                        .filter((record: any) => record.output_type?.toLowerCase() === 'workflow_results')
                        .map((record: any, index: number) => {
                          let parsedMetrics: any = {};
                          try {
                            parsedMetrics = typeof record.metrics === 'string' ? JSON.parse(record.metrics) : record.metrics || {};
                          } catch (e) {
                            console.error('Failed to parse workflow results metrics:', e);
                          }

                          return (
                            <Box key={record.id || index}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                                <Chip
                                  label={record.status || 'Unknown'}
                                  size="small"
                                  color={record.status === 'LOAD_COMPLETED' ? 'success' : 'default'}
                                />
                                <Typography variant="body2" color="text.secondary">
                                  Duration: <strong>{record.duration_mins || 'N/A'}</strong>
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                                  {record.created_at
                                    ? new Date(record.created_at).toLocaleString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                      })
                                    : 'N/A'}
                                </Typography>
                              </Box>

                              {record.status === 'LOAD_TRIGGERED' && !parsedMetrics.brand_vs_nonbrand && !parsedMetrics.topic_subtopic ? (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    p: 2,
                                    backgroundColor: alpha(theme.palette.info.main, 0.05),
                                    borderRadius: 1,
                                    border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                                  }}
                                >
                                  <CircularProgress size={16} thickness={5} />
                                  <Typography variant="body2" color="text.secondary">
                                    Processing workflow results — data will appear shortly.
                                  </Typography>
                                </Box>
                              ) : (parsedMetrics.brand_vs_nonbrand || parsedMetrics.topic_subtopic) ? (
                                <TableContainer>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell sx={{ fontWeight: 600, backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>Rank</TableCell>
                                        <TableCell sx={{ fontWeight: 600, backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>Topic</TableCell>
                                        <TableCell sx={{ fontWeight: 600, backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>Subtopic</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600, backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>Keywords</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600, backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>Impressions</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600, backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>Clicks</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600, backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>Cost</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {parsedMetrics.brand_vs_nonbrand && Object.entries(parsedMetrics.brand_vs_nonbrand).map(([key, value]: [string, any]) => (
                                        <TableRow key={`brand-${key}`}>
                                          <TableCell>-</TableCell>
                                          <TableCell>
                                            <Chip 
                                              label={key} 
                                              size="small" 
                                              color={key === 'BRANDED' ? 'primary' : 'secondary'}
                                              sx={{ fontWeight: 600 }}
                                            />
                                          </TableCell>
                                          <TableCell>-</TableCell>
                                          <TableCell align="right">{value.distinct_keywords?.toLocaleString()}</TableCell>
                                          <TableCell align="right">{value.impressions?.toLocaleString()}</TableCell>
                                          <TableCell align="right">{value.clicks?.toLocaleString()}</TableCell>
                                          <TableCell align="right">${value.cost?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                                        </TableRow>
                                      ))}
                                      
                                      {parsedMetrics.brand_vs_nonbrand && parsedMetrics.topic_subtopic && (
                                        <TableRow>
                                          <TableCell colSpan={7} sx={{ backgroundColor: alpha(theme.palette.divider, 0.1), py: 0.5 }} />
                                        </TableRow>
                                      )}
                                      
                                      {parsedMetrics.topic_subtopic && parsedMetrics.topic_subtopic.map((item: any, idx: number) => (
                                        <TableRow key={`topic-${idx}`}>
                                          <TableCell>{item.rank}</TableCell>
                                          <TableCell>{item.topic}</TableCell>
                                          <TableCell>{item.subtopic}</TableCell>
                                          <TableCell align="right">{item.distinct_keywords?.toLocaleString()}</TableCell>
                                          <TableCell align="right">{item.impressions?.toLocaleString()}</TableCell>
                                          <TableCell align="right">{item.clicks?.toLocaleString()}</TableCell>
                                          <TableCell align="right">${item.cost?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              ) : (
                                <Box
                                  sx={{
                                    p: 2,
                                    backgroundColor: alpha(theme.palette.warning.main, 0.05),
                                    borderRadius: 1,
                                    border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                                  }}
                                >
                                  <Typography variant="body2" color="text.secondary">
                                    No workflow results data found. Expected structure with brand_vs_nonbrand and/or topic_subtopic.
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          );
                        })}
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        py: 4,
                        gap: 1,
                        backgroundColor: alpha(theme.palette.grey[500], 0.04),
                        borderRadius: 1,
                        border: `1px dashed ${alpha(theme.palette.grey[500], 0.2)}`,
                      }}
                    >
                      <PendingIcon sx={{ fontSize: 32, color: 'grey.400' }} />
                      <Typography variant="body2" color="text.secondary" fontWeight={500}>
                        Awaiting workflow results
                      </Typography>
                      <Typography variant="caption" color="text.disabled">
                        Results will appear here once processing is complete
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* SERP Results */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                    SERP Results
                  </Typography>

                  {statusMetrics.ingestion_status_metrics.filter((r: any) => r.output_type?.toLowerCase() === 'serp_results').length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {statusMetrics.ingestion_status_metrics
                        .filter((record: any) => record.output_type?.toLowerCase() === 'serp_results')
                        .map((record: any, index: number) => {
                          let parsedMetrics: any = {};
                          try {
                            parsedMetrics = typeof record.metrics === 'string' ? JSON.parse(record.metrics) : record.metrics || {};
                          } catch (e) {
                            console.error('Failed to parse SERP metrics:', e);
                          }

                          return (
                            <Box key={record.id || index}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                                <Chip
                                  label={record.status || 'Unknown'}
                                  size="small"
                                  color={record.status === 'LOAD_COMPLETED' ? 'success' : 'default'}
                                />
                                <Typography variant="body2" color="text.secondary">
                                  Duration: <strong>{record.duration_mins || 'N/A'}</strong>
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                                  {record.created_at
                                    ? new Date(record.created_at).toLocaleString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                      })
                                    : 'N/A'}
                                </Typography>
                              </Box>

                              {record.status === 'LOAD_TRIGGERED' && !parsedMetrics.serp_feature_categories_by_impressions ? (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    p: 2,
                                    backgroundColor: alpha(theme.palette.info.main, 0.05),
                                    borderRadius: 1,
                                    border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                                  }}
                                >
                                  <CircularProgress size={16} thickness={5} />
                                  <Typography variant="body2" color="text.secondary">
                                    Processing SERP results — data will appear shortly.
                                  </Typography>
                                </Box>
                              ) : parsedMetrics.serp_feature_categories_by_impressions ? (
                                <TableContainer sx={{ maxHeight: 500 }}>
                                  <Table size="small" stickyHeader>
                                    <TableHead>
                                      <TableRow>
                                        <TableCell sx={{ fontWeight: 600, backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>Rank</TableCell>
                                        <TableCell sx={{ fontWeight: 600, backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>Category</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600, backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>Keywords</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600, backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>Impressions</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {parsedMetrics.serp_feature_categories_by_impressions.map((item: any, idx: number) => (
                                        <TableRow key={idx}>
                                          <TableCell>{item.rank}</TableCell>
                                          <TableCell>{item.category}</TableCell>
                                          <TableCell align="right">{item.distinct_keywords?.toLocaleString()}</TableCell>
                                          <TableCell align="right">{item.impressions?.toLocaleString()}</TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              ) : (
                                <Box
                                  sx={{
                                    p: 2,
                                    backgroundColor: alpha(theme.palette.warning.main, 0.05),
                                    borderRadius: 1,
                                    border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                                  }}
                                >
                                  <Typography variant="body2" color="text.secondary">
                                    No SERP feature categories data found.
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          );
                        })}
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        py: 4,
                        gap: 1,
                        backgroundColor: alpha(theme.palette.grey[500], 0.04),
                        borderRadius: 1,
                        border: `1px dashed ${alpha(theme.palette.grey[500], 0.2)}`,
                      }}
                    >
                      <PendingIcon sx={{ fontSize: 32, color: 'grey.400' }} />
                      <Typography variant="body2" color="text.secondary" fontWeight={500}>
                        Awaiting SERP results
                      </Typography>
                      <Typography variant="caption" color="text.disabled">
                        Results will appear here once processing is complete
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* DataForSEO Results */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                    DataForSEO Results
                  </Typography>

                  {statusMetrics.ingestion_status_metrics.filter((r: any) => r.output_type?.toLowerCase() === 'dataforseo_results').length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {statusMetrics.ingestion_status_metrics
                        .filter((record: any) => record.output_type?.toLowerCase() === 'dataforseo_results')
                        .map((record: any, index: number) => {
                          let parsedMetrics: any = {};
                          try {
                            parsedMetrics = typeof record.metrics === 'string' ? JSON.parse(record.metrics) : record.metrics || {};
                          } catch (e) {
                            console.error('Failed to parse DataForSEO metrics:', e);
                          }

                          return (
                            <Box key={record.id || index}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                                <Chip
                                  label={record.status || 'Unknown'}
                                  size="small"
                                  color={record.status === 'LOAD_COMPLETED' ? 'success' : 'default'}
                                />
                                <Typography variant="body2" color="text.secondary">
                                  Duration: <strong>{record.duration_mins || 'N/A'}</strong>
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                                  {record.created_at
                                    ? new Date(record.created_at).toLocaleString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                      })
                                    : 'N/A'}
                                </Typography>
                              </Box>

                              {record.status === 'LOAD_TRIGGERED' && !parsedMetrics.top_queries_by_avg_monthly_searches ? (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    p: 2,
                                    backgroundColor: alpha(theme.palette.info.main, 0.05),
                                    borderRadius: 1,
                                    border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                                  }}
                                >
                                  <CircularProgress size={16} thickness={5} />
                                  <Typography variant="body2" color="text.secondary">
                                    Processing DataForSEO results — data will appear shortly.
                                  </Typography>
                                </Box>
                              ) : parsedMetrics.top_queries_by_avg_monthly_searches ? (
                                <TableContainer sx={{ maxHeight: 500 }}>
                                  <Table size="small" stickyHeader>
                                    <TableHead>
                                      <TableRow>
                                        <TableCell sx={{ fontWeight: 600, backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>Rank</TableCell>
                                        <TableCell sx={{ fontWeight: 600, backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>Keyword</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600, backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>Monthly Searches</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600, backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>Impressions</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600, backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>Clicks</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600, backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>Cost</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {parsedMetrics.top_queries_by_avg_monthly_searches.map((item: any, idx: number) => (
                                        <TableRow key={idx}>
                                          <TableCell>{item.rank}</TableCell>
                                          <TableCell>{item.keyword}</TableCell>
                                          <TableCell align="right">{item.average_monthly_searches?.toLocaleString()}</TableCell>
                                          <TableCell align="right">{item.impressions?.toLocaleString()}</TableCell>
                                          <TableCell align="right">{item.clicks?.toLocaleString()}</TableCell>
                                          <TableCell align="right">${item.cost?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              ) : (
                                <Box
                                  sx={{
                                    p: 2,
                                    backgroundColor: alpha(theme.palette.warning.main, 0.05),
                                    borderRadius: 1,
                                    border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                                  }}
                                >
                                  <Typography variant="body2" color="text.secondary">
                                    No DataForSEO top queries data found.
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          );
                        })}
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        py: 4,
                        gap: 1,
                        backgroundColor: alpha(theme.palette.grey[500], 0.04),
                        borderRadius: 1,
                        border: `1px dashed ${alpha(theme.palette.grey[500], 0.2)}`,
                      }}
                    >
                      <PendingIcon sx={{ fontSize: 32, color: 'grey.400' }} />
                      <Typography variant="body2" color="text.secondary" fontWeight={500}>
                        Awaiting DataForSEO results
                      </Typography>
                      <Typography variant="caption" color="text.disabled">
                        Results will appear here once processing is complete
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Explore Results Button */}
            <Grid item xs={12}>
              {(() => {
                const metrics = statusMetrics.ingestion_status_metrics;

                const isCompleted = (type: string) => {
                  const record = metrics.find((r: any) => r.output_type?.toLowerCase() === type);
                  return !!record && record.status === 'LOAD_COMPLETED';
                };

                const workflowReady = isCompleted('workflow_results');
                const serpReady = isCompleted('serp_results');
                const dataforseoReady = isCompleted('dataforseo_results');
                const allReady = workflowReady && serpReady && dataforseoReady;

                const tooltipParts: string[] = [];
                if (!workflowReady) tooltipParts.push('Workflow Results');
                if (!serpReady) tooltipParts.push('SERP Results');
                if (!dataforseoReady) tooltipParts.push('DataForSEO Results');
                const tooltipMsg = tooltipParts.length
                  ? `Still processing: ${tooltipParts.join(', ')}`
                  : '';

                return (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 2, gap: 1 }}>
                    <Tooltip
                      title={tooltipMsg}
                      placement="top"
                    >
                      <span>
                        <Button
                          variant="contained"
                          size="large"
                          startIcon={<ExploreResultsIcon />}
                          onClick={handleNavigateToExplore}
                          disabled={!allReady}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            minWidth: 280,
                            px: 6,
                            py: 1.5,
                            borderRadius: 2,
                            background: allReady
                              ? `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.light} 90%)`
                              : undefined,
                            boxShadow: allReady ? `0 3px 8px ${alpha(theme.palette.primary.main, 0.3)}` : 'none',
                            '&:hover': allReady
                              ? {
                                  background: `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.primary.main} 90%)`,
                                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.4)}`,
                                  transform: 'translateY(-2px)',
                                }
                              : {},
                            transition: 'all 0.2s ease-in-out',
                          }}
                        >
                          Explore Results
                        </Button>
                      </span>
                    </Tooltip>
                    {!allReady && (
                      <Typography variant="caption" color="text.disabled">
                        Waiting for all sections to complete before results can be explored
                      </Typography>
                    )}
                  </Box>
                );
              })()}
            </Grid>
            </Grid>
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No metrics data available
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

export default WorkflowSummaryStep;
